import React, { useState, useRef } from 'react';
import { Sparkle, UploadSimple, FileText, X, CircleNotch, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { projectService, type ProjectExtendedFields } from '../../../services/project.service';
import { toast } from 'sonner';
import posthog from '../../../posthog';

interface DocumentUploadPanelProps {
  projectId: string;
  onAnalyzed: (coreFieldsJson: string, extendedFields: ProjectExtendedFields, documentText: string) => void;
}

export function DocumentUploadPanel({ projectId, onAnalyzed }: DocumentUploadPanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const selected = Array.from(newFiles);
    setFiles((prev) => [...prev, ...selected]);
    setStatusMessage(null);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleAnalyze = async () => {
    if (files.length === 0 || !projectId) return;

    setAnalyzing(true);
    setStatusMessage('Analyzing documents with Gemini AI...');

    try {
      const response = await projectService.analyzeProjectDocuments(projectId, files);
      const jsonCoreString = JSON.stringify(response.core_fields, null, 2);
      onAnalyzed(jsonCoreString, response.extended_fields, response.document_text || '');
      posthog.capture('project_documents_analyzed', { file_count: files.length });
      toast.success('Document analysis complete! Form pre-filled & AI metadata saved.');
      setStatusMessage('Analysis complete!');
      setFiles([]);
    } catch (err: any) {
      const msg = err.message || 'Failed to analyze documents. Please try again.';
      toast.error(msg);
      setStatusMessage(`Error: ${msg}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-surface-hover border border-border p-4 rounded-2xl space-y-3 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-[9px] font-bold text-slate-450 uppercase tracking-wider flex items-center gap-1.5">
            <Sparkle className="w-3.5 h-3.5 text-accent animate-pulse" weight="fill" />
            AI Document Analysis & Pre-fill
          </label>
          <span className="text-[9px] text-slate-400 font-medium">
            Upload campaign documents (PDF, DOCX, PPTX, TXT, images) for automatic field extraction
          </span>
        </div>
      </div>

      {/* Drag & Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
          isDragging
            ? 'border-accent bg-accent/5'
            : 'border-slate-200 hover:border-slate-300 bg-white/50 hover:bg-white'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.pptx,.txt,image/*"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
        />
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent">
            <UploadSimple className="w-4 h-4" />
          </div>
          <p className="text-[11px] font-semibold text-slate-700">
            Click to upload or drag & drop files
          </p>
          <p className="text-[9px] text-slate-400 font-medium">
            Supports PDF, Word, PowerPoint, Text, and Images
          </p>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between bg-white border border-slate-100 p-2 rounded-xl text-[10px]"
            >
              <div className="flex items-center gap-2 min-w-0 pr-2">
                <FileText className="w-4 h-4 text-accent shrink-0" />
                <span className="font-medium text-slate-700 truncate">{file.name}</span>
                <span className="text-slate-400 text-[9px] shrink-0">({formatFileSize(file.size)})</span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
                disabled={analyzing}
                className="text-slate-400 hover:text-rose-500 transition-colors p-1"
                title="Remove file"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Action button & status */}
      <div className="flex items-center justify-between pt-1">
        {statusMessage ? (
          <span className="text-[9px] font-medium flex items-center gap-1.5 text-slate-500">
            {analyzing && <CircleNotch className="w-3 h-3 text-accent animate-spin" />}
            {!analyzing && statusMessage.startsWith('Error') && (
              <WarningCircle className="w-3 h-3 text-rose-500 shrink-0" />
            )}
            {!analyzing && !statusMessage.startsWith('Error') && (
              <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
            )}
            <span className={statusMessage.startsWith('Error') ? 'text-rose-600' : 'text-slate-600'}>
              {statusMessage}
            </span>
          </span>
        ) : (
          <span />
        )}

        <button
          type="button"
          onClick={handleAnalyze}
          disabled={files.length === 0 || analyzing}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-accent text-white font-semibold text-[10px] shadow-sm hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {analyzing ? (
            <>
              <CircleNotch className="w-3.5 h-3.5 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkle className="w-3.5 h-3.5" weight="fill" />
              Analyze ({files.length} {files.length === 1 ? 'file' : 'files'})
            </>
          )}
        </button>
      </div>
    </div>
  );
}
